// Appointments data + mutations (PRD §4.14). Read from tenants/{t}/appointments
// (members read); writes via member-guarded callables. Notifications go through
// the send pipeline.
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useTenant } from './tenant';

export type ApptStatus = 'booked' | 'confirmed' | 'cancelled' | 'completed';
export interface Appointment {
  id: string;
  customerName: string;
  phoneE164: string;
  service: string | null;
  startAt: number;
  durationMin: number;
  notes: string | null;
  status: ApptStatus;
}

export function useAppointments(): { appts: Appointment[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!tenantId) { setAppts([]); setLoading(false); return; }
    const q = query(collection(db, `tenants/${tenantId}/appointments`), orderBy('startAt', 'asc'));
    return onSnapshot(q, (snap) => {
      setAppts(snap.docs.map((d) => {
        const a = d.data() as Partial<Appointment>;
        return {
          id: d.id, customerName: a.customerName ?? '', phoneE164: a.phoneE164 ?? '',
          service: a.service ?? null, startAt: a.startAt ?? 0, durationMin: a.durationMin ?? 30,
          notes: a.notes ?? null, status: (a.status as ApptStatus) ?? 'booked',
        };
      }));
      setLoading(false);
    }, () => setLoading(false));
  }, [tenantId]);
  return { appts, loading };
}

export interface ApptInput { id?: string; customerName: string; phoneE164: string; service?: string; startAt: number; durationMin: number; notes?: string; }
const saveFn = httpsCallable<ApptInput, { ok: boolean; id: string }>(functions, 'appointmentSave');
const statusFn = httpsCallable<{ id: string; status: ApptStatus }, { ok: boolean }>(functions, 'appointmentSetStatus');
const deleteFn = httpsCallable<{ id: string }, { ok: boolean }>(functions, 'appointmentDelete');
const notifyFn = httpsCallable<{ id: string; body: string }, { ok: boolean; status: unknown }>(functions, 'appointmentNotify');

export const saveAppointment = (i: ApptInput) => saveFn(i).then((r) => r.data);
export const setAppointmentStatus = (id: string, status: ApptStatus) => statusFn({ id, status }).then((r) => r.data);
export const deleteAppointment = (id: string) => deleteFn({ id }).then((r) => r.data);
export const notifyAppointment = (id: string, body: string) => notifyFn({ id, body }).then((r) => r.data);
